import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// --- Types (mirror backend src/modules/email/email.types.ts) --------------

export type EmailUrgency = 'high' | 'medium' | 'low';

export interface EmailAttachment {
  filename: string;
  mimeType: string;
  size: number;
  attachmentId: string;
}

export interface EmailInvoiceSignal {
  vendor: string;
  hint: string | null;
  attachment: EmailAttachment | null;
}

export interface EmailSummaryItem {
  id: string;
  gmailMessageId: string;
  gmailThreadId: string | null;
  subject: string;
  fromName: string;
  fromEmail: string;
  receivedAt: string;
  isUnread: boolean;
  summary: string;
  context: string;
  urgency: EmailUrgency;
  suggestedActions: string[];
  invoice: EmailInvoiceSignal | null;
  attachments: EmailAttachment[];
}

export interface EmailInsights {
  executiveSummary: string;
  suggestedActions: string[];
  alerts: string[];
}

export interface EmailSummary {
  date: string;
  connected: boolean;
  isOwner: boolean;
  emails: EmailSummaryItem[];
  insights: EmailInsights | null;
  invoiceCount: number;
  total: number;
}

/** Staged invoice attachment ready for the ingestion flow. */
export interface StagedInvoice {
  id: string;
  fileName: string;
  contentType: string;
  size: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Fetch the Daily Email Summary for a user/date (CLE-233). */
export async function getEmailSummary(userId: string, date?: string): Promise<EmailSummary> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  const res = await apiFetch<Envelope<EmailSummary>>(`/api/daily/${userId}/emails${qs}`, {
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * Trigger the invoice-ingest action for a detected invoice email (CLE-233 §4):
 * the backend re-fetches the PDF read-only and stages it, returning a staged
 * attachment the financial ingestion flow consumes.
 */
export async function ingestEmailInvoice(
  userId: string,
  gmailMessageId: string,
  attachmentId: string,
): Promise<StagedInvoice> {
  const res = await apiFetch<Envelope<StagedInvoice>>(
    `/api/daily/${userId}/emails/${encodeURIComponent(gmailMessageId)}/ingest-invoice`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ attachmentId }),
    },
  );
  return res.data;
}
